import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {Member} from '../../hooks/useDomainData';
import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';
import {displayNameFromPerson, Person} from '../../hooks/usePersonInfo';
import {useAlerts} from 'react-native-paper-alerts';

export interface MemberEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  member?: Member;
  allPeople?: Array<Person>;
}

export function MemberEditor(props: MemberEditorProps): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();
  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();

  const [validating, setValidating] = useState(false);

  const collectionName = 'members';
  const saveFirestoreObject = useMutation(
    (firestoreObject: Member) => {
      // clean out properties that might be undefined
      if (firestoreObject.memberNotes === undefined) {
        delete firestoreObject.memberNotes;
      }
      if (props.member === undefined) {
        // console.log(
        //   'adding: ' + JSON.stringify(firestoreObject, null, 2),
        // );

        // If there was no date set, delete the date property totally

        return (
          firestore()
            .collection(collectionName)
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(firestoreObject) as Promise<unknown> as Promise<void>
        );
      }

      // console.log(
      //   'saving: ' + JSON.stringify(firestoreObject, null, 2),
      // );
      return firestore()
        .collection(collectionName)
        .doc(props.member?.ref.id)
        .set(firestoreObject, {merge: true});
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let member: {[key: string]: FormikStoreType} = {
    memberId: Date.now(),
    memberName: undefined,
    personIds: [],
  };

  if (props.member !== undefined) {
    member.memberId = props.member.memberId;
    member.memberName = props.member.memberName;
    if (props.member.memberNotes) {
      member.memberNotes = props.member.memberNotes;
    }
    member.personIds = [...props.member.personIds];
  }

  return (
    <Portal>
      <Modal
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${props.member ? 'Edit' : 'Create'} Member`,
              firestoreObject: member,
              fieldMetadata: [
                {
                  key: 'memberId',
                  name: 'Member ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'Member ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'memberName',
                  name: 'Member Name',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'Member Name is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'personIds',
                  name: 'People',
                  type: FormikStoreTypes.arraySelect,
                  selectItems: {
                    documents: props.allPeople ?? [],
                    items: props.allPeople?.map(person => person.ref) ?? [],
                    labelExtractor: person =>
                      displayNameFromPerson(person as Person),
                  },
                  required: true,
                  errorText:
                    'Members must have People. Add them first if necessary',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'memberNotes',
                  name: 'Member Notes',
                  type: FormikStoreTypes.textarea,
                  required: false,
                  errorText: 'Member Notes is a required field',
                  validator: DefaultFormikStoreValidator,
                },
              ],
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              validate: false,
              onPress: () => {
                props.setDialogVisible(false);
              },
            },
            {
              text: 'Save',
              validate: true,
              onPress: formObjects => {
                // console.log(
                //   'saving with ' +
                //     JSON.stringify(formObjects[0].firestoreObject, null, 2),
                // );
                setValidating(true);
                saveFirestoreObject.mutate(
                  formObjects[0].firestoreObject as unknown as Member,
                );
                props.setDialogVisible(false);
              },
            },
          ]}
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
